import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Paper,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';

const STYLE_ID = 'dvn-widget-style';

const FALLBACK_PACKAGE = {
  id: 'fixture-pkg',
  title: 'Reference Kyoto Heritage Walk',
  short_description: 'Five days of temples, tea, and night markets — sandbox package.',
  cover_image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1600&q=80&auto=format',
  destination: 'Kyoto, Japan',
  package_type: 'cultural',
  duration_days: 5,
  base_price: '2890.00',
  currency: 'USD',
};

const FALLBACK_TRIP = {
  id: 1000529,
  title: 'Reference Amalfi escape',
  description: 'Sample trip created via POST /v1/trips (docs sandbox).',
  destination: 'Positano, Italy',
  trip_type: 'family',
  image_url: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1200&q=80&auto=format',
  dates_label: '12 Oct – 19 Oct 2026',
  duration_days: 8,
  member_count: 2,
  status_label: 'Upcoming',
};

const WIDGET_CSS = [
  '.dvn-widget{',
  '--dvn-font-display:Georgia,"Times New Roman",serif;',
  '--dvn-font-ui:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;',
  '--dvn-color-ink:#1a1210;',
  '--dvn-color-muted:#666;',
  '--dvn-color-body:#555;',
  '--dvn-color-surface:#fff;',
  '--dvn-color-border:#d9d0c4;',
  '--dvn-color-row-border:#ebe4da;',
  '--dvn-color-accent:#c9a961;',
  '--dvn-color-badge-bg:#c9a961;',
  '--dvn-color-badge-text:#2a070c;',
  '--dvn-color-cta-bg:#6b1d2a;',
  '--dvn-color-cta-text:#fff;',
  '--dvn-color-media-bg:#6b1d2a;',
  '--dvn-grid-min:260px;',
  '--dvn-grid-gap:20px;',
  '--dvn-card-radius:0px;',
  'font-family:var(--dvn-font-display);color:var(--dvn-color-ink);line-height:1.4;box-sizing:border-box',
  '}',
  '.dvn-widget[data-dvn-theme="dark"]{',
  '--dvn-color-ink:#f6efe4;',
  '--dvn-color-muted:rgba(246,239,228,.72);',
  '--dvn-color-body:rgba(246,239,228,.82);',
  '--dvn-color-surface:#1f1418;',
  '--dvn-color-border:#4a3a40;',
  '--dvn-color-row-border:#3a2d32;',
  '--dvn-color-accent:#c9a961;',
  '--dvn-color-badge-bg:#c9a961;',
  '--dvn-color-badge-text:#2a070c;',
  '--dvn-color-cta-bg:#8b2435;',
  '--dvn-color-cta-text:#fff;',
  '--dvn-color-media-bg:#2a070c;',
  '}',
  '.dvn-widget *,.dvn-widget *::before,.dvn-widget *::after{box-sizing:border-box}',
  '.dvn-widget-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(var(--dvn-grid-min),1fr));gap:var(--dvn-grid-gap)}',
  '.dvn-card{display:flex;flex-direction:column;background:var(--dvn-color-surface);border:1px solid var(--dvn-color-border);border-radius:var(--dvn-card-radius);overflow:hidden}',
  '.dvn-card-media{position:relative;aspect-ratio:16/9;background:var(--dvn-color-media-bg);overflow:hidden}',
  '.dvn-card-media img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}',
  '.dvn-card-media::after{content:"";position:absolute;inset:0;background:linear-gradient(to top,rgba(18,5,8,.72) 0%,rgba(18,5,8,.18) 48%,transparent 74%);pointer-events:none}',
  '.dvn-badge{position:absolute;top:12px;left:12px;z-index:2;background:var(--dvn-color-badge-bg);color:var(--dvn-color-badge-text);font-family:var(--dvn-font-ui);font-size:10px;letter-spacing:.14em;text-transform:uppercase;padding:4px 8px;border-radius:calc(var(--dvn-card-radius) * 0.35)}',
  '.dvn-cover-meta{position:absolute;left:12px;bottom:12px;z-index:2;color:#fff;font-family:var(--dvn-font-ui);font-size:11px;letter-spacing:.12em;text-transform:uppercase}',
  '.dvn-card-body{padding:16px 16px 12px;display:flex;flex-direction:column;gap:8px;flex:1}',
  '.dvn-eyebrow{font-family:var(--dvn-font-ui);font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--dvn-color-accent);margin:0}',
  '.dvn-card h3{margin:0;font-size:22px;font-weight:400;line-height:1.25;font-family:var(--dvn-font-display)}',
  '.dvn-loc{margin:0;font-family:var(--dvn-font-ui);font-size:13px;color:var(--dvn-color-muted)}',
  '.dvn-desc{margin:0;font-family:var(--dvn-font-ui);font-size:13px;color:var(--dvn-color-body);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}',
  '.dvn-rows{margin:4px 0 0;padding:0;list-style:none;font-family:var(--dvn-font-ui);font-size:13px}',
  '.dvn-rows li{display:flex;justify-content:space-between;gap:12px;padding:8px 0;border-top:1px solid var(--dvn-color-row-border);color:var(--dvn-color-muted)}',
  '.dvn-rows li span:last-child{color:var(--dvn-color-ink);font-weight:600;text-align:right}',
  '.dvn-card-footer{display:flex;justify-content:flex-end;padding:0 16px 16px;margin-top:auto}',
  '.dvn-card a.dvn-cta,.dvn-card button.dvn-cta{display:inline-block;background:var(--dvn-color-cta-bg);color:var(--dvn-color-cta-text);text-decoration:none;padding:10px 14px;font-family:var(--dvn-font-ui);font-size:12px;letter-spacing:.08em;text-transform:uppercase;border-radius:var(--dvn-card-radius);border:0;cursor:default}',
  '.dvn-card a.dvn-cta:hover,.dvn-card button.dvn-cta:hover{opacity:.92}',
].join('');

const LIGHT_DEFAULTS = {
  theme: 'light',
  radius: 0,
  ctaBg: '#6b1d2a',
  ctaText: '#ffffff',
  accent: '#c9a961',
  badgeText: '#2a070c',
  surface: '#ffffff',
  border: '#d9d0c4',
  ink: '#1a1210',
  muted: '#666666',
  mediaBg: '#6b1d2a',
  fontDisplay: 'Georgia, "Times New Roman", serif',
};

const DARK_DEFAULTS = {
  theme: 'dark',
  radius: 0,
  ctaBg: '#8b2435',
  ctaText: '#ffffff',
  accent: '#c9a961',
  badgeText: '#2a070c',
  surface: '#1f1418',
  border: '#4a3a40',
  ink: '#f6efe4',
  muted: '#b9a8a0',
  mediaBg: '#2a070c',
  fontDisplay: 'Georgia, "Times New Roman", serif',
};

const SHAPE_PRESETS = [
  { id: 'sharp', radius: 0 },
  { id: 'soft', radius: 8 },
  { id: 'rounded', radius: 16 },
  { id: 'pill', radius: 24 },
];

const FONT_PRESETS = [
  { id: 'serif', value: 'Georgia, "Times New Roman", serif' },
  { id: 'system', value: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' },
  { id: 'mono', value: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' },
];

function escapeHtml(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildPackageCardHtml(pkg, ctaLabel) {
  const image = pkg?.cover_image || '';
  const badge = pkg?.package_type
    ? `<span class="dvn-badge">${escapeHtml(pkg.package_type)}</span>`
    : '';
  const coverMeta = pkg?.duration_days
    ? `<div class="dvn-cover-meta">${escapeHtml(`${pkg.duration_days}-day package`)}</div>`
    : '';
  const location = pkg?.destination || pkg?.destination_country || '';
  const rows = [
    location ? `<li><span>Destination</span><span>${escapeHtml(location)}</span></li>` : '',
    pkg?.duration_days
      ? `<li><span>Duration</span><span>${escapeHtml(`${pkg.duration_days} days`)}</span></li>`
      : '',
    pkg?.base_price
      ? `<li><span>Price</span><span>${escapeHtml(`${pkg.currency || 'USD'} ${pkg.base_price} per person`)}</span></li>`
      : '',
  ].filter(Boolean).join('');

  return (
    `<article class="dvn-card" data-sandbox-card="package">`
    + `<div class="dvn-card-media">`
    + (image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" />` : '')
    + badge
    + coverMeta
    + `</div>`
    + `<div class="dvn-card-body">`
    + (pkg?.package_type ? `<p class="dvn-eyebrow">${escapeHtml(pkg.package_type)}</p>` : '')
    + `<h3>${escapeHtml(pkg?.title || 'Package')}</h3>`
    + (location ? `<p class="dvn-loc">${escapeHtml(location)}</p>` : '')
    + (pkg?.short_description ? `<p class="dvn-desc">${escapeHtml(pkg.short_description)}</p>` : '')
    + (rows ? `<ul class="dvn-rows">${rows}</ul>` : '')
    + `</div>`
    + `<div class="dvn-card-footer">`
    + `<button type="button" class="dvn-cta" data-sandbox-action="sample-package">${escapeHtml(ctaLabel)}</button>`
    + `</div>`
    + `</article>`
  );
}

function buildTripCardHtml(trip, ctaLabel) {
  const image = trip?.image_url || '';
  const badge = trip?.trip_type
    ? `<span class="dvn-badge">${escapeHtml(trip.trip_type)}</span>`
    : '';
  const coverMeta = trip?.status_label || trip?.status
    ? `<div class="dvn-cover-meta">${escapeHtml(trip.status_label || trip.status)}</div>`
    : '';
  const location = trip?.destination || '';
  const rows = [
    trip?.dates_label ? `<li><span>Dates</span><span>${escapeHtml(trip.dates_label)}</span></li>` : '',
    trip?.duration_days
      ? `<li><span>Duration</span><span>${escapeHtml(`${trip.duration_days} days`)}</span></li>`
      : '',
    trip?.member_count
      ? `<li><span>Travelers</span><span>${escapeHtml(`${trip.member_count} members`)}</span></li>`
      : '',
  ].filter(Boolean).join('');
  const tripRef = escapeHtml(String(trip?.id || '1000529'));

  return (
    `<article class="dvn-card" data-sandbox-card="trip">`
    + `<div class="dvn-card-media">`
    + (image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" />` : '')
    + badge
    + coverMeta
    + `</div>`
    + `<div class="dvn-card-body">`
    + (trip?.trip_type ? `<p class="dvn-eyebrow">${escapeHtml(trip.trip_type)}</p>` : '')
    + `<h3>${escapeHtml(trip?.title || 'Trip')}</h3>`
    + (location ? `<p class="dvn-loc">${escapeHtml(location)}</p>` : '')
    + (trip?.description ? `<p class="dvn-desc">${escapeHtml(trip.description)}</p>` : '')
    + (rows ? `<ul class="dvn-rows">${rows}</ul>` : '')
    + `</div>`
    + `<div class="dvn-card-footer">`
    + `<button type="button" class="dvn-cta" data-sandbox-action="open-trip" data-trip-ref="${tripRef}">${escapeHtml(ctaLabel)}</button>`
    + `</div>`
    + `</article>`
  );
}

function toCssVars(state) {
  return {
    '--dvn-card-radius': `${state.radius}px`,
    '--dvn-color-cta-bg': state.ctaBg,
    '--dvn-color-cta-text': state.ctaText,
    '--dvn-color-accent': state.accent,
    '--dvn-color-badge-bg': state.accent,
    '--dvn-color-badge-text': state.badgeText,
    '--dvn-color-surface': state.surface,
    '--dvn-color-border': state.border,
    '--dvn-color-row-border': state.border,
    '--dvn-color-ink': state.ink,
    '--dvn-color-muted': state.muted,
    '--dvn-color-body': state.muted,
    '--dvn-color-media-bg': state.mediaBg,
    '--dvn-font-display': state.fontDisplay,
  };
}

function ensureWidgetStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = WIDGET_CSS;
  document.head.appendChild(style);
}

function buildSnippet(state) {
  const attrs = ['data-dvn-catalog="YOUR_CATALOG_TOKEN"'];
  if (state.theme === 'dark') attrs.push('data-dvn-theme="dark"');
  const vars = [
    `--dvn-card-radius: ${state.radius}px;`,
    `--dvn-color-cta-bg: ${state.ctaBg};`,
    `--dvn-color-cta-text: ${state.ctaText};`,
    `--dvn-color-accent: ${state.accent};`,
    `--dvn-color-badge-bg: ${state.accent};`,
    `--dvn-color-badge-text: ${state.badgeText};`,
    `--dvn-color-surface: ${state.surface};`,
    `--dvn-color-border: ${state.border};`,
    `--dvn-color-ink: ${state.ink};`,
    `--dvn-color-muted: ${state.muted};`,
    `--dvn-color-body: ${state.muted};`,
    `--dvn-color-row-border: ${state.border};`,
    `--dvn-color-media-bg: ${state.mediaBg};`,
    `--dvn-font-display: ${state.fontDisplay};`,
  ].join('\n    ');
  return `<div
  ${attrs.join('\n  ')}
  style="
    ${vars}
  "
></div>`;
}

const codeSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12.5,
  lineHeight: 1.55,
  bgcolor: DOCUMENT.ivory,
  border: `1px solid ${DOCUMENT.rule}`,
  p: 2,
  overflowX: 'auto',
  whiteSpace: 'pre',
  color: DOCUMENT.ink,
  m: 0,
};

const sectionLabelSx = {
  fontSize: 11,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink3,
  mb: 1,
  mt: 0.5,
};

const controlLabelSx = {
  fontSize: 12,
  color: DOCUMENT.ink2,
  mb: 0.5,
  display: 'block',
};

const toggleSx = {
  borderRadius: 0,
  textTransform: 'none',
  px: 1.25,
  py: 0.5,
  fontSize: 12.5,
  borderColor: DOCUMENT.ruleHard,
  '&.Mui-selected': {
    bgcolor: DOCUMENT.oxblood,
    color: DOCUMENT.ivory,
    '&:hover': { bgcolor: DOCUMENT.oxblood },
  },
};

function ColorControl({ id, label, value, onChange, testId }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography component="label" htmlFor={id} sx={controlLabelSx}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          component="input"
          id={id}
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          sx={{
            width: 40,
            height: 36,
            border: `1px solid ${DOCUMENT.ruleHard}`,
            p: 0,
            bgcolor: 'transparent',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        />
        <TextField
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ 'aria-label': label }}
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 0, fontFamily: 'monospace', fontSize: 13 } }}
        />
      </Box>
    </Box>
  );
}

const SandboxWidgetStyleDemo = ({
  packageFixture,
  tripFixture,
  onOpenTrip,
  onStyleChange,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState(LIGHT_DEFAULTS);
  const [copied, setCopied] = useState(false);
  const pkg = packageFixture || FALLBACK_PACKAGE;
  const trip = tripFixture || FALLBACK_TRIP;

  useEffect(() => {
    ensureWidgetStyles();
  }, []);

  const setField = (key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  const cardsHtml = useMemo(() => {
    const pkgHtml = buildPackageCardHtml(
      pkg,
      t('developerPortal.sandbox.openSamplePackage')
    );
    const tripHtml = buildTripCardHtml(
      trip,
      t('developerPortal.sandbox.openSampleTrip')
    );
    return `${pkgHtml}${tripHtml}`;
  }, [pkg, trip, t]);

  const previewStyle = useMemo(() => toCssVars(state), [state]);

  useEffect(() => {
    if (typeof onStyleChange === 'function') {
      onStyleChange({
        theme: state.theme,
        style: previewStyle,
      });
    }
  }, [onStyleChange, state.theme, previewStyle]);

  const snippet = useMemo(() => buildSnippet(state), [state]);

  const shapeId = SHAPE_PRESETS.find((s) => s.radius === state.radius)?.id || null;
  const fontId = FONT_PRESETS.find((f) => f.value === state.fontDisplay)?.id || 'serif';

  const applyTheme = (_, next) => {
    if (!next) return;
    setState(next === 'dark' ? { ...DARK_DEFAULTS } : { ...LIGHT_DEFAULTS, radius: state.radius });
  };

  const applyShape = (_, next) => {
    if (!next) return;
    const preset = SHAPE_PRESETS.find((s) => s.id === next);
    if (preset) setField('radius', preset.radius);
  };

  const applyFont = (_, next) => {
    if (!next) return;
    const preset = FONT_PRESETS.find((f) => f.id === next);
    if (preset) setField('fontDisplay', preset.value);
  };

  const applyBrand = () => {
    setState({
      ...LIGHT_DEFAULTS,
      radius: 12,
      ctaBg: '#004080',
      ctaText: '#ffffff',
      accent: '#d4af37',
      badgeText: '#1a1208',
      surface: '#ffffff',
      border: '#cfc4a8',
      ink: '#1a1210',
      muted: '#5c5346',
      mediaBg: '#003366',
    });
  };

  const reset = () => {
    setState(state.theme === 'dark' ? { ...DARK_DEFAULTS } : { ...LIGHT_DEFAULTS });
  };

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const onPreviewClick = (event) => {
    const button = event.target?.closest?.('[data-sandbox-action]');
    if (!button) return;
    const action = button.getAttribute('data-sandbox-action');
    if (action === 'open-trip') {
      const tripRef = button.getAttribute('data-trip-ref');
      if (tripRef && typeof onOpenTrip === 'function') {
        onOpenTrip({ tripRef, href: null });
      }
    }
  };

  return (
    <Paper
      elevation={0}
      data-testid="sandbox-widget-style-demo"
      sx={{
        bgcolor: DOCUMENT.surface,
        border: `1px solid ${DOCUMENT.ruleHard}`,
        borderRadius: 0,
        p: { xs: 2.5, md: 3 },
        mb: 3,
      }}
    >
      <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
        {t('developerPortal.sandbox.styleDemoTitle')}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, mb: 2.5, maxWidth: '68ch' }}>
        {t('developerPortal.sandbox.styleDemoBody')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '340px minmax(0, 1fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Box
          data-testid="sandbox-style-controls"
          sx={{
            border: `1px solid ${DOCUMENT.rule}`,
            bgcolor: DOCUMENT.ivory,
            p: 2,
            maxHeight: { lg: '70vh' },
            overflowY: 'auto',
          }}
        >
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 1.5 }}>
            {t('developerPortal.sandbox.styleControlsTitle')}
          </Typography>

          <Typography sx={sectionLabelSx}>{t('developerPortal.sandbox.styleSectionTheme')}</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={state.theme}
            onChange={applyTheme}
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            <ToggleButton value="light" data-testid="sandbox-style-theme-light" sx={toggleSx}>
              {t('developerPortal.sandbox.styleThemeLight')}
            </ToggleButton>
            <ToggleButton value="dark" data-testid="sandbox-style-theme-dark" sx={toggleSx}>
              {t('developerPortal.sandbox.styleThemeDark')}
            </ToggleButton>
          </ToggleButtonGroup>

          <Typography sx={sectionLabelSx}>{t('developerPortal.sandbox.styleSectionShape')}</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={shapeId}
            onChange={applyShape}
            sx={{ mb: 1, flexWrap: 'wrap' }}
          >
            {SHAPE_PRESETS.map((shape) => (
              <ToggleButton
                key={shape.id}
                value={shape.id}
                data-testid={`sandbox-style-shape-${shape.id}`}
                sx={toggleSx}
              >
                {t(`developerPortal.sandbox.styleShape.${shape.id}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography sx={{ ...controlLabelSx, mb: 0 }}>
            {t('developerPortal.sandbox.styleControlRadius')} — {state.radius}px
          </Typography>
          <Slider
            value={state.radius}
            min={0}
            max={28}
            step={1}
            onChange={(_e, value) => setField('radius', Number(value))}
            data-testid="sandbox-style-radius"
            sx={{ color: DOCUMENT.oxblood, mb: 2, mt: 0.5 }}
          />

          <Typography sx={sectionLabelSx}>{t('developerPortal.sandbox.styleSectionType')}</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={fontId}
            onChange={applyFont}
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            {FONT_PRESETS.map((font) => (
              <ToggleButton key={font.id} value={font.id} sx={toggleSx}>
                {t(`developerPortal.sandbox.styleFont.${font.id}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Divider sx={{ my: 1.5, borderColor: DOCUMENT.rule }} />
          <Typography sx={sectionLabelSx}>{t('developerPortal.sandbox.styleSectionColors')}</Typography>

          <ColorControl
            id="sandbox-cta-bg"
            label={t('developerPortal.sandbox.styleControlCta')}
            value={state.ctaBg}
            onChange={(v) => setField('ctaBg', v)}
            testId="sandbox-style-cta"
          />
          <ColorControl
            id="sandbox-cta-text"
            label={t('developerPortal.sandbox.styleControlCtaText')}
            value={state.ctaText}
            onChange={(v) => setField('ctaText', v)}
            testId="sandbox-style-cta-text"
          />
          <ColorControl
            id="sandbox-accent"
            label={t('developerPortal.sandbox.styleControlAccent')}
            value={state.accent}
            onChange={(v) => setField('accent', v)}
            testId="sandbox-style-accent"
          />
          <ColorControl
            id="sandbox-badge-text"
            label={t('developerPortal.sandbox.styleControlBadgeText')}
            value={state.badgeText}
            onChange={(v) => setField('badgeText', v)}
            testId="sandbox-style-badge-text"
          />
          <ColorControl
            id="sandbox-surface"
            label={t('developerPortal.sandbox.styleControlSurface')}
            value={state.surface}
            onChange={(v) => setField('surface', v)}
            testId="sandbox-style-surface"
          />
          <ColorControl
            id="sandbox-border"
            label={t('developerPortal.sandbox.styleControlBorder')}
            value={state.border}
            onChange={(v) => setField('border', v)}
            testId="sandbox-style-border"
          />
          <ColorControl
            id="sandbox-ink"
            label={t('developerPortal.sandbox.styleControlInk')}
            value={state.ink}
            onChange={(v) => setField('ink', v)}
            testId="sandbox-style-ink"
          />
          <ColorControl
            id="sandbox-muted"
            label={t('developerPortal.sandbox.styleControlMuted')}
            value={state.muted}
            onChange={(v) => setField('muted', v)}
            testId="sandbox-style-muted"
          />
          <ColorControl
            id="sandbox-media"
            label={t('developerPortal.sandbox.styleControlMedia')}
            value={state.mediaBg}
            onChange={(v) => setField('mediaBg', v)}
            testId="sandbox-style-media"
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={applyBrand}
              data-testid="sandbox-style-preset-brand"
              sx={{ borderRadius: 0, textTransform: 'none', borderColor: DOCUMENT.ruleHard, color: DOCUMENT.ink }}
            >
              {t('developerPortal.sandbox.stylePreset.brand')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={reset}
              data-testid="sandbox-style-reset"
              sx={{ borderRadius: 0, textTransform: 'none', borderColor: DOCUMENT.ruleHard, color: DOCUMENT.ink }}
            >
              {t('developerPortal.sandbox.styleControlReset')}
            </Button>
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.5 }}>
            {t('developerPortal.sandbox.styleDemoPreview')}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 1.5, maxWidth: '60ch' }}>
            {t('developerPortal.sandbox.styleDemoPreviewHint')}
          </Typography>
          <Box
            className="dvn-widget"
            data-dvn-theme={state.theme === 'dark' ? 'dark' : undefined}
            style={previewStyle}
            data-testid="sandbox-style-preview"
            onClick={onPreviewClick}
            sx={{
              p: 2,
              mb: 2.5,
              bgcolor: state.theme === 'dark' ? '#12080c' : DOCUMENT.ivory,
              border: `1px solid ${DOCUMENT.rule}`,
              '& .dvn-widget-grid': {
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              },
              '& .dvn-cta[data-sandbox-action="open-trip"]': {
                cursor: 'pointer',
              },
            }}
            dangerouslySetInnerHTML={{
              __html: `<div class="dvn-widget-grid">${cardsHtml}</div>`,
            }}
          />

          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.5 }}>
            {t('developerPortal.sandbox.styleDemoSnippet')}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 1, maxWidth: '60ch' }}>
            {t('developerPortal.sandbox.styleDemoSnippetHint')}
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <Box component="pre" sx={codeSx} data-testid="sandbox-style-snippet">{snippet}</Box>
            <Button
              size="small"
              onClick={copySnippet}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                minWidth: 0,
                px: 1.25,
                fontSize: 12,
                textTransform: 'none',
                color: DOCUMENT.ink2,
                bgcolor: DOCUMENT.surface,
                border: `1px solid ${DOCUMENT.rule}`,
                borderRadius: 0,
              }}
            >
              {copied ? t('developerPortal.docs.copied') : t('developerPortal.docs.copy')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default SandboxWidgetStyleDemo;
